import * as React from 'react';

import { enumUnit, Unit } from '@rematter/frontend-lib-api-client';
import {
  convertFromPounds,
  convertToPounds,
  IconButton,
  shortenUnit,
  weightToPounds,
} from '@rematter/ui-web';
import { cn, humanReadableShortPricingUnit } from '@rematter/ui-web/lib';
import { useSetAtom } from 'jotai';
import { Trash2 } from 'lucide-react';

import { NumericInputCell } from '../NumericInputCell';

import { AddDeductionButton } from './AddDeductionButton';
import { AddNotesButton } from './AddNotesButton';
import { gridLayoutMaterialReconcilitation } from './constants/gridLayout';
import { ReceivedLineItemDialog } from './ReceivedLineItemDialog';

import {
  $addDeduction,
  $removeReceivedMaterialItem,
  $updateReceivedMaterialItem,
  ReceivedLineItemForm as ReceivedLineItemFormType,
} from '~/modules/trade/_state/loadReconciliation.state';
import { useTranslations } from '~/modules/trade/i18n/useTranslations';
import { WeightUnitType } from '~/modules/trade/types/load';
import { zeroMoney } from '~/modules/trade/utils/money';
import { convertWeightForPricing } from '~/modules/trade/utils/weight';
import { InventoryItemSelect } from '~/shared/components/line-items';
import { SelectEnum } from '~/shared/components/select-fields/SelectEnum';

type FilterableOption = {
  id: string;
  group?: string;
  label: string;
  mnemonic: string;
  disabled?: boolean;
  extraInfo?: string;
  dialogMode?: boolean;
};

interface ReceivedLineItemFormProps {
  receivedItem: ReceivedLineItemFormType;
  itemOptions: FilterableOption[];
  dialogMode?: boolean;
  disabled?: boolean;
  weightUnit?: WeightUnitType;
  facilityDefaultUnit?: Unit;
}

export const ReceivedLineItemForm = ({
  receivedItem,
  itemOptions,
  dialogMode = true,
  disabled = false,
  weightUnit = 'price',
  facilityDefaultUnit = enumUnit.Pounds,
}: ReceivedLineItemFormProps) => {
  const t = useTranslations();
  const updateReceivedItem = useSetAtom($updateReceivedMaterialItem);
  const removeReceivedItem = useSetAtom($removeReceivedMaterialItem);
  const addDeduction = useSetAtom($addDeduction);
  const [isDialogOpen, setIsDialogOpen] = React.useState(false);

  // Get pricing unit from receivedItem, defaulting to Pounds
  const pricingUnit = receivedItem.pricingUnit ?? enumUnit.Pounds;

  // Determine display unit based on weight unit toggle and THIS row's pricing unit
  const displayUnit = React.useMemo(() => {
    const currentPricingUnit = pricingUnit ?? enumUnit.Pounds;

    // Exception: "Each" always displays as "Each" regardless of toggle
    if (currentPricingUnit === enumUnit.Each) return enumUnit.Each;

    // Otherwise respect the toggle: 'price' uses pricing unit, 'scale' uses facility default
    return weightUnit === 'price' ? currentPricingUnit : facilityDefaultUnit;
  }, [pricingUnit, weightUnit, facilityDefaultUnit]);

  // Weight display unit abbreviation
  const massUnitAbbreviation = humanReadableShortPricingUnit(displayUnit);

  // Convert stored weight value to display unit for showing in UI
  const convertToDisplayUnit = React.useCallback(
    (
      value: number | null | undefined,
      storedUnit: Unit = enumUnit.Pounds,
    ): number => {
      if (!value) return 0;

      // If units are the same, no conversion needed
      if (storedUnit === displayUnit) return value;

      // Convert: stored unit → pounds → display unit
      const pounds = convertToPounds(value, storedUnit);
      return convertFromPounds(pounds, displayUnit);
    },
    [displayUnit],
  );

  // Convert display value back to stored unit for saving
  const convertFromDisplayUnit = React.useCallback(
    (value: number, storedUnit: Unit = enumUnit.Pounds): number => {
      // If units are the same, no conversion needed
      if (storedUnit === displayUnit) return value;

      // Convert: display unit → pounds → stored unit
      const pounds = convertToPounds(value, displayUnit);
      return convertFromPounds(pounds, storedUnit);
    },
    [displayUnit],
  );

  const handleFieldChange = (
    field: keyof Omit<ReceivedLineItemFormType, 'id' | 'receivedLineItemId'>,
    value: number | string | null | undefined | Record<string, unknown>,
  ) => {
    if (field === 'unitPrice') {
      updateReceivedItem(receivedItem.id, {
        [field]: {
          ...(receivedItem.unitPrice || {
            currencyCode: 'USD' as const,
            __typename: 'Money' as const,
          }),
          amount:
            typeof value === 'number'
              ? value
              : value
                ? parseFloat(String(value))
                : 0,
        },
      });
    } else if (field === 'grossWeight' || field === 'tareWeight') {
      if (typeof value === 'number') {
        // Convert from display unit back to Pounds for storage
        const convertedValue = convertFromDisplayUnit(value, enumUnit.Pounds);
        updateReceivedItem(receivedItem.id, {
          [field]: {
            value: convertedValue,
            unit: enumUnit.Pounds,
            __typename: 'Weight' as const,
          },
        });
      } else if (typeof value === 'object' && value !== null) {
        updateReceivedItem(receivedItem.id, {
          [field]: {
            ...(value as Record<string, unknown>),
            unit: enumUnit.Pounds,
            __typename: 'Weight' as const,
          },
        });
      }
    } else {
      updateReceivedItem(receivedItem.id, { [field]: value });
    }
  };

  // Convert weights to display unit for showing in UI
  const grossWeightValue = convertToDisplayUnit(
    receivedItem.grossWeight?.value,
    (receivedItem.grossWeight?.unit as Unit) || enumUnit.Pounds,
  );
  const tareWeightValue = convertToDisplayUnit(
    receivedItem.tareWeight?.value,
    (receivedItem.tareWeight?.unit as Unit) || enumUnit.Pounds,
  );
  const netQuantityValue = grossWeightValue - tareWeightValue;

  // Calculate price using proper unit conversion for pricing
  // Convert net weight to pricing unit for calculation (weights are stored in Pounds)
  const netQuantityPounds =
    weightToPounds(receivedItem.grossWeight) -
    weightToPounds(receivedItem.tareWeight);
  const totalValue = Number(
    (
      convertWeightForPricing(netQuantityPounds, pricingUnit) *
      (receivedItem.unitPrice?.amount || 0)
    ).toFixed(2),
  );

  const handleDialogClose = () => {
    setIsDialogOpen(false);
  };

  // Find the item name for the dialog title
  const selectedItem = React.useMemo(
    () => itemOptions.find((item) => item.id === receivedItem.itemId),
    [itemOptions, receivedItem.itemId],
  );
  const dialogTitle = selectedItem
    ? selectedItem.label
    : t.materialsReconciliation.dialogs.receivedItemTitle;

  const handleDeductionTypeSelect = React.useCallback(
    (deductionTypeId: string) => {
      addDeduction({
        receivedLineItemId: receivedItem.id,
        deductionTypeId,
        weightQuantity: {
          value: 0,
          unit: enumUnit.Pounds,
          __typename: 'Weight',
        },
        priceQuantity: zeroMoney(),
      });
    },
    [receivedItem.id, addDeduction],
  );

  return (
    <div className="@ReceivedLineItemForm contents">
      <div
        className={cn(
          'grid w-full items-center gap-2',
          gridLayoutMaterialReconcilitation,
        )}
      >
        {/* Column 1: Item Select */}
        <InventoryItemSelect
          value={receivedItem.itemId ?? undefined}
          options={itemOptions}
          onChange={(id) => handleFieldChange('itemId', id)}
          disabled={disabled}
        />
        {/* Column 2: Unit Price */}
        <NumericInputCell
          value={receivedItem.unitPrice?.amount}
          onChange={(v) => handleFieldChange('unitPrice', v)}
          prefix="$"
          minDecimalPlaces={2}
          maxDecimalPlaces={8}
          formatNumber
          isDisabled={disabled}
        />
        {/* Column 3: Pricing Unit */}
        <SelectEnum<Unit>
          enumValue={enumUnit}
          formatOptionLabel={shortenUnit}
          value={pricingUnit}
          onChange={(v) => {
            handleFieldChange('pricingUnit', v ?? enumUnit.Pounds);
          }}
          isDisabled={disabled}
        />
        {/* Column 4: Gross Weight */}
        <NumericInputCell
          value={grossWeightValue}
          onChange={(v) => {
            // Handler will convert from display unit back to Pounds
            handleFieldChange('grossWeight', v || 0);
          }}
          prefix="G"
          suffix={massUnitAbbreviation}
          isDisabled={disabled}
        />
        {/* Column 5: Tare Weight */}
        <NumericInputCell
          value={tareWeightValue}
          onChange={(v) => {
            // Handler will convert from display unit back to Pounds
            handleFieldChange('tareWeight', v || 0);
          }}
          prefix="T"
          suffix={massUnitAbbreviation}
          isDisabled={disabled}
        />
        {/* Column 6: Net Quantity */}
        <NumericInputCell
          value={netQuantityValue}
          isDisabled={true}
          prefix="N"
          suffix={massUnitAbbreviation}
        />

        {/* Column 7: Total Value */}
        <NumericInputCell value={totalValue} isDisabled={true} prefix="$" />
        {/* Column 8: Add Deduction Button */}
        <AddDeductionButton
          onAddDeduction={handleDeductionTypeSelect}
          isDisabled={disabled}
          iconButton={true}
        />
        {/* Column 9: Add Notes Button */}
        <AddNotesButton
          isDisabled={disabled}
          initialValue={receivedItem.notes}
          onAddNotes={(notes) => handleFieldChange('notes', notes)}
        />
        {/* Column 10: Delete Received Item */}
        <IconButton
          icon={<Trash2 size={18} />}
          variant="outline"
          className="size-[40px]"
          color="failure"
          aria-label="Delete received line item"
          onPress={() => removeReceivedItem(receivedItem.id)}
          isDisabled={disabled}
        />
      </div>

      {/* Dialog for non-dialog mode */}
      {!dialogMode && (
        <ReceivedLineItemDialog
          receivedItem={receivedItem}
          title={dialogTitle}
          isOpen={isDialogOpen}
          onClose={handleDialogClose}
          disabled={disabled}
        />
      )}
    </div>
  );
};
